import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Search, SlidersHorizontal, Sparkles } from 'lucide-react';
import { api, toQueryString } from '@/lib/api';
import { useFilters, type Filters } from '@/hooks/useFilters';
import { useBrandReveal, useReference } from '@/hooks/useApp';
import { useSeo, useSeoCatalogue, useSeoContent } from '@/hooks/useSeo';
import { useHeightAnchor } from '@/hooks/useHeightAnchor';
import { SEARCH_EXAMPLES, useSmartSearch } from '@/hooks/useSmartSearch';
import { CarCard, CarCardSkeleton } from '@/components/CarCard';
import { FilterPanel } from '@/components/FilterPanel';
import { BrandMark } from '@/components/BrandMark';
import { Button, Chip, EmptyState, Input, Select, Sheet } from '@/components/ui';
import { formatListingCount, formatPrice } from '@/lib/format';
import { BUY_GUIDE_SLUG, SELL_GUIDE_SLUG, SITE_NAME, categoryPath } from '@/lib/seo';
import { cn } from '@/lib/cn';
import type { ListingSummary, Make, PagedResult, ReferenceData } from '@/lib/types';

const SORT_OPTIONS = [
  { value: 0, label: 'Najnoviji' },
  { value: 1, label: 'Cena: rastuće' },
  { value: 2, label: 'Cena: opadajuće' },
  { value: 3, label: 'Kilometraža: rastuće' },
  { value: 4, label: 'Godište: novije' },
  { value: 5, label: 'Godište: starije' },
];

interface ActiveChip {
  id: string;
  label: string;
  remove: () => void;
}

type EnumArrayKey =
  | 'fuelTypes'
  | 'bodyTypes'
  | 'transmissions'
  | 'driveTypes'
  | 'conditions'
  | 'damageStatuses'
  | 'origins'
  | 'sellerTypes';

/**
 * Every active filter, as a removable pill above the results.
 *
 * On a phone the filter panel is behind a button, so without this row there is nothing on screen to
 * say why only three cars came back — and the fix is buried two taps away.
 */
function useActiveChips(
  filters: Filters,
  reference: ReferenceData | undefined,
  setFilters: (update: Partial<Filters>) => void,
): ActiveChip[] {
  return useMemo(() => {
    const chips: ActiveChip[] = [];

    const enumChips = <K extends EnumArrayKey>(
      key: K,
      options: { value: number; label: string }[] | undefined,
    ) => {
      for (const value of filters[key]) {
        chips.push({
          id: `${key}-${value}`,
          label: options?.find((option) => option.value === value)?.label ?? String(value),
          remove: () =>
            setFilters({ [key]: filters[key].filter((item) => item !== value) } as Partial<Filters>),
        });
      }
    };

    if (filters.q) {
      chips.push({ id: 'q', label: `„${filters.q}”`, remove: () => setFilters({ q: undefined }) });
    }

    for (const id of filters.makeIds) {
      const make = reference?.makes.find((item) => item.id === id);
      const owned = new Set(
        (reference?.models ?? []).filter((model) => model.makeId === id).map((model) => model.id),
      );

      chips.push({
        id: `make-${id}`,
        label: make?.name ?? String(id),
        // Dropping the marque drops its models with it, exactly as it does inside the panel.
        remove: () =>
          setFilters({
            makeIds: filters.makeIds.filter((item) => item !== id),
            modelIds: filters.modelIds.filter((item) => !owned.has(item)),
          }),
      });
    }

    for (const id of filters.modelIds) {
      const model = reference?.models.find((item) => item.id === id);
      const make = reference?.makes.find((item) => item.id === model?.makeId);

      chips.push({
        id: `model-${id}`,
        // Qualified with the marque, so a "7" in this row is unmistakably the BMW.
        label: make ? `${make.name} ${model?.name}` : (model?.name ?? String(id)),
        remove: () => setFilters({ modelIds: filters.modelIds.filter((item) => item !== id) }),
      });
    }

    for (const id of filters.cityIds) {
      const city = reference?.cities.find((item) => item.id === id);
      chips.push({
        id: `city-${id}`,
        label: city?.name ?? String(id),
        remove: () => setFilters({ cityIds: filters.cityIds.filter((item) => item !== id) }),
      });
    }

    if (filters.priceFrom !== undefined) {
      chips.push({
        id: 'priceFrom',
        label: `od ${formatPrice(filters.priceFrom)}`,
        remove: () => setFilters({ priceFrom: undefined }),
      });
    }
    if (filters.priceTo !== undefined) {
      chips.push({
        id: 'priceTo',
        label: `do ${formatPrice(filters.priceTo)}`,
        remove: () => setFilters({ priceTo: undefined }),
      });
    }
    if (filters.yearFrom !== undefined) {
      chips.push({
        id: 'yearFrom',
        label: `od ${filters.yearFrom}.`,
        remove: () => setFilters({ yearFrom: undefined }),
      });
    }
    if (filters.yearTo !== undefined) {
      chips.push({
        id: 'yearTo',
        label: `do ${filters.yearTo}.`,
        remove: () => setFilters({ yearTo: undefined }),
      });
    }
    if (filters.mileageFrom !== undefined) {
      chips.push({
        id: 'mileageFrom',
        label: `od ${filters.mileageFrom} km`,
        remove: () => setFilters({ mileageFrom: undefined }),
      });
    }
    if (filters.mileageTo !== undefined) {
      chips.push({
        id: 'mileageTo',
        label: `do ${filters.mileageTo} km`,
        remove: () => setFilters({ mileageTo: undefined }),
      });
    }
    if (filters.powerKwFrom !== undefined) {
      chips.push({
        id: 'powerFrom',
        label: `od ${filters.powerKwFrom} kW`,
        remove: () => setFilters({ powerKwFrom: undefined }),
      });
    }
    if (filters.powerKwTo !== undefined) {
      chips.push({
        id: 'powerTo',
        label: `do ${filters.powerKwTo} kW`,
        remove: () => setFilters({ powerKwTo: undefined }),
      });
    }
    if (filters.engineCcFrom !== undefined) {
      chips.push({
        id: 'engineCcFrom',
        label: `od ${filters.engineCcFrom} cm³`,
        remove: () => setFilters({ engineCcFrom: undefined }),
      });
    }
    if (filters.engineCcTo !== undefined) {
      chips.push({
        id: 'engineCcTo',
        label: `do ${filters.engineCcTo} cm³`,
        remove: () => setFilters({ engineCcTo: undefined }),
      });
    }

    enumChips('fuelTypes', reference?.enums.fuelTypes);
    enumChips('bodyTypes', reference?.enums.bodyTypes);
    enumChips('transmissions', reference?.enums.transmissions);
    enumChips('driveTypes', reference?.enums.driveTypes);
    enumChips('conditions', reference?.enums.conditions);
    enumChips('damageStatuses', reference?.enums.damageStatuses);
    enumChips('origins', reference?.enums.origins);
    enumChips('sellerTypes', reference?.enums.sellerTypes);

    if (filters.withPhotosOnly) {
      chips.push({
        id: 'withPhotosOnly',
        label: 'Sa slikama',
        remove: () => setFilters({ withPhotosOnly: undefined }),
      });
    }
    if (filters.registeredOnly) {
      chips.push({
        id: 'registeredOnly',
        label: 'Registrovan',
        remove: () => setFilters({ registeredOnly: undefined }),
      });
    }
    if (filters.serviceBook) {
      chips.push({
        id: 'serviceBook',
        label: 'Servisna knjiga',
        remove: () => setFilters({ serviceBook: undefined }),
      });
    }
    if (filters.firstOwner) {
      chips.push({
        id: 'firstOwner',
        label: 'Prvi vlasnik',
        remove: () => setFilters({ firstOwner: undefined }),
      });
    }
    if (filters.exchangeAccepted) {
      chips.push({
        id: 'exchangeAccepted',
        label: 'Zamena',
        remove: () => setFilters({ exchangeAccepted: undefined }),
      });
    }

    if (filters.equipment) {
      chips.push({
        id: 'equipment',
        label: 'Oprema',
        remove: () => setFilters({ equipment: 0 }),
      });
    }

    return chips;
  }, [filters, reference, setFilters]);
}

/** The marques worth one tap from the landing page. Only ever adds — removal lives in the chips row. */
function BrandRail({ onPick }: { onPick: (make: Make) => void }) {
  const { data: reference } = useReference();
  const { reveal } = useBrandReveal();

  const popular = (reference?.makes ?? []).filter((make) => make.isPopular).slice(0, 14);

  if (popular.length === 0) {
    return null;
  }

  return (
    <div className="fade-edges-x scrollbar-none -mx-4 flex gap-2 overflow-x-auto px-4 pb-1">
      {popular.map((make, index) => (
        <button
          key={make.id}
          type="button"
          onClick={() => {
            reveal(make);
            onPick(make);
          }}
          style={{ '--i': Math.min(index, 13) } as React.CSSProperties}
          className={cn(
            'animate-fade-in-up stagger press flex shrink-0 items-center gap-2 rounded-full border py-1.5 pr-4 pl-1.5 text-sm font-medium',
            'border-default bg-raised text-muted transition duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]',
            'hover:-translate-y-0.5 hover:border-strong hover:text-body hover:shadow-card',
          )}
        >
          <BrandMark make={make} size="sm" />
          {make.name}
        </button>
      ))}
    </div>
  );
}

export function SearchPage() {
  const {
    filters,
    setFilters,
    replaceAll,
    toggleInArray,
    toggleEquipment,
    clear,
    activeCount,
    searchParams,
  } = useFilters();
  const { data: reference } = useReference();

  const [sheetOpen, setSheetOpen] = useState(false);

  /*
    The hero above the results collapses the first time a filter is applied, and every one of the
    four hundred pixels it gives up comes from directly above the filter panel. Anchoring it means
    the panel, the chips and the grid stay exactly where they were while that happens — the two
    things that change its shape are whether any filter is set at all, and whether the marque rail
    still has a reason to be there.
  */
  const hero = useRef<HTMLElement>(null);
  useHeightAnchor(
    hero,
    `${activeCount > 0}:${filters.makeIds.length > 0}:${filters.q !== undefined}`,
  );

  const smart = useSmartSearch(replaceAll, searchParams.toString());

  /*
    Whatever the buyer typed, whether it survived as a sentence or turned into filters.

    The box is uncontrolled between submissions — a search only runs when it is submitted — but the
    term can also disappear from the URL without the box being touched: filters have remove buttons,
    "Poništi sve" clears everything, and the back button walks out of a search. Reconciling during
    render rather than in an effect means the field is never briefly wrong on screen.
  */
  const typed = filters.qs ?? filters.q ?? '';
  const [searchText, setSearchText] = useState(typed);
  const [lastTyped, setLastTyped] = useState(typed);

  if (lastTyped !== typed) {
    setLastTyped(typed);
    setSearchText(typed);
  }

  // `qs` is the buyer's sentence rather than a filter, so it is the one thing here the API is not
  // asked about; it exists to keep the box filled and the search shareable.
  const request = toQueryString({ ...filters, qs: undefined, pageSize: 24 });

  const query = useQuery({
    queryKey: ['listings', request],
    queryFn: () => api<PagedResult<ListingSummary>>(`/api/listings?${request}`),
    // Without this the grid empties to skeletons on every filter change, and the page jumps as it
    // collapses and re-expands. Keeping the previous page visible while the next loads is what makes
    // filtering feel immediate.
    placeholderData: keepPreviousData,
  });

  const chips = useActiveChips(filters, reference, setFilters);
  const results = query.data;

  /*
    The home page's own head, restored on the way back from a listing.

    The canonical is always "/" whatever filters are set. A filtered search is a view of the same
    collection, and the number of distinct URLs the filter panel can produce is effectively
    unbounded — letting each one be its own indexable page is how a marketplace with ten thousand
    cars reports a million pages and gets trusted for none of them.
  */
  useSeo({
    title: `Polovni automobili — besplatni oglasi | ${SITE_NAME}`,
    description:
      'Polovni automobili u Srbiji na jednom mestu. Brz i lak pronalazak auta, bez reklama i bez ' +
      'naplate. Kupite ili prodajte auto — oglas je uvek besplatan.',
    canonicalPath: '/',
  });

  const goToPage = (page: number) => {
    setFilters({ page });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div>
      {/* ---------------------------------------------------------------------------------- */}
      {/* Hero. Collapses to a plain search bar once a search is under way, because a landing */}
      {/* headline above someone's twelfth refinement is just something to scroll past.      */}
      {/* ---------------------------------------------------------------------------------- */}
      <section ref={hero} className="relative overflow-hidden">
        <div className="aurora">
          <span />
        </div>

        <div
          className={cn(
            'relative mx-auto max-w-7xl px-4',
            activeCount > 0 ? 'pt-6 pb-4' : 'pt-10 pb-8 sm:pt-16 sm:pb-10',
          )}
        >
          {activeCount === 0 && (
            <div className="animate-fade-in-up mx-auto mb-8 max-w-2xl text-center">
              <h1 className="text-4xl leading-[1.05] font-bold text-balance text-body sm:text-5xl">
                Pronađite <span className="text-accent">svoja</span> kola
              </h1>

              {/*
                The line that says, in words a search engine reads and a visitor believes, what this
                is. The headline above it is the brand; this is the proposition — and it is where
                "polovni automobili" and "pronalazak auta" belong, because that is what someone
                typed to get here.
              */}
              <p className="mx-auto mt-3 max-w-xl text-[15px] leading-relaxed text-balance text-muted">
                Besplatni oglasi za polovne automobile u Srbiji. Brz i lak pronalazak auta — bez
                reklama, bez naplate.
              </p>
            </div>
          )}

          {/*
            One box, for both kinds of search.

            There used to be two — a keyword field and a "describe it in your own words" field — and
            the split asked the buyer to work out which of them their sentence belonged in before
            they were allowed to search. "Audi Q8 50000km" belongs in both and worked in neither.
          */}
          <form
            onSubmit={(event) => {
              event.preventDefault();

              // Emptying the box and pressing the button drops the search term and keeps the
              // filters, which is the only thing it can sensibly mean.
              if (searchText.trim().length === 0) {
                setFilters({ q: undefined, qs: undefined });
                return;
              }

              smart.search(searchText);
            }}
            className="animate-fade-in-up mx-auto max-w-3xl"
          >
            <div className="relative">
              <Search
                size={18}
                className="pointer-events-none absolute top-1/2 left-5 -translate-y-1/2 text-faint"
              />
              <Input
                type="search"
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                placeholder="Audi Q8, „džip do 8.000 €”, „brzo mlađe nemca”…"
                aria-label="Pretraga oglasa"
                maxLength={400}
                className="h-14 rounded-2xl border-transparent bg-raised pr-28 pl-13 text-base shadow-card hover:shadow-card-hover sm:h-16 sm:rounded-[1.25rem] sm:pl-14"
              />
              <Button
                type="submit"
                size="sm"
                loading={smart.pending}
                className="absolute top-1/2 right-2.5 -translate-y-1/2 sm:h-11"
              >
                Traži
              </Button>
            </div>
          </form>

          {/*
            Shown while the buyer's own sentence is still what drove the page, whether it became
            filters or stayed as text. The server only sends a message when the chips cannot say the
            thing themselves: that the sentence could not be read at all, that part of it was
            dropped, or that a phrase about what the car is for was answered with a specific list of
            models. A dropped filter is precisely the one no chip can show, and a row of eight marque
            chips with no sentence above it looks like the search misfired.
          */}
          {typed !== '' && smart.message && (
            <p className="animate-fade-in-down mx-auto mt-2 flex max-w-3xl items-center gap-1.5 text-xs text-faint">
              <Sparkles size={12} className="shrink-0 text-gold" />
              {smart.message}
            </p>
          )}

          {/*
            The examples are the only thing on the page that says a whole sentence is allowed here.
            Without them the box looks like every other keyword field and gets used like one.
          */}
          {activeCount === 0 && (
            <div className="mx-auto mt-3 flex max-w-3xl flex-wrap justify-center gap-1.5">
              {SEARCH_EXAMPLES.map((example) => (
                <button
                  key={example}
                  type="button"
                  onClick={() => {
                    setSearchText(example);
                    smart.search(example);
                  }}
                  className="press rounded-full border border-default bg-raised/60 px-3 py-1 text-[11.5px] text-muted transition hover:border-accent hover:text-accent"
                >
                  {example}
                </button>
              ))}
            </div>
          )}

          {filters.makeIds.length === 0 && (
            <div className="mx-auto mt-6 max-w-3xl">
              <BrandRail onPick={(make) => setFilters({ makeIds: [make.id] })} />
            </div>
          )}
        </div>
      </section>

      {/*
        The results start clear of the hero rather than flush against it. The hero has its own
        background and the filter panel has a border, so with no gap between them the panel reads as
        a piece of the hero that has come loose.
      */}
      <div className="mx-auto max-w-7xl px-4 pt-5 pb-10 sm:pt-8">
        <div className="flex gap-7">
          {/* Sticky on desktop so filters stay reachable however far the results scroll. */}
          <aside className="hidden w-80 shrink-0 lg:block">
            {/*
              Two boxes rather than one, and deliberately so: the frame owns the border, the rounding
              and the background, the box inside it owns the scrolling.

              One box cannot do both. A custom scrollbar is painted inside the border box but is not
              clipped to the border radius, so a rounded panel that scrolls itself grows a grey bar
              that hangs outside its own top corner and past the curve. Insetting the scroller by the
              frame's padding puts the bar back inside the curve where it belongs.
            */}
            <div className="sticky top-20 rounded-[var(--radius-panel)] border border-default bg-raised p-1.5 shadow-card">
              <div className="scroll-gutter-even max-h-[calc(100dvh-7rem)] overflow-y-auto overscroll-contain px-2 pb-2">
                <FilterPanel
                  filters={filters}
                  setFilters={setFilters}
                  toggleInArray={toggleInArray}
                  toggleEquipment={toggleEquipment}
                  clear={clear}
                  activeCount={activeCount}
                />
              </div>
            </div>
          </aside>

          <div className="min-w-0 flex-1">
            {/*
              Sticky on phones. The filters, the count and the sort are what a buyer reaches for
              while scrolling a long list, and on a small screen they otherwise scroll away after
              the first six cars and stay away for the next three hundred.
            */}
            <div className="glass sticky top-16 z-20 -mx-4 mb-4 flex items-center gap-2 px-4 py-2.5 sm:static sm:mx-0 sm:mb-4 sm:gap-2.5 sm:bg-transparent sm:px-0 sm:py-0 sm:backdrop-blur-none">
              <Button
                variant="secondary"
                size="sm"
                className="shrink-0 lg:hidden"
                onClick={() => setSheetOpen(true)}
              >
                <SlidersHorizontal size={16} />
                Filteri
                {activeCount > 0 && (
                  <span className="ml-0.5 flex size-4.5 items-center justify-center rounded-full bg-accent text-[10px] font-bold text-on-accent">
                    {activeCount}
                  </span>
                )}
              </Button>

              {/* Hidden on the narrowest phones, where the sort control needs the room more. */}
              <p
                className="hidden min-w-0 truncate text-sm text-muted tabular-nums sm:block"
                aria-live="polite"
              >
                {query.isPending ? 'Učitavanje…' : results ? formatListingCount(results.total) : ''}
              </p>

              <Select
                value={filters.sort}
                onChange={(event) => setFilters({ sort: Number(event.target.value) })}
                aria-label="Sortiranje"
                className="ml-auto h-10 w-auto min-w-0 max-w-[10rem] shrink text-[13px] sm:min-w-40 sm:text-sm"
              >
                {SORT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </div>

            {chips.length > 0 && (
              <div className="mb-4 flex flex-wrap items-center gap-1.5">
                {chips.map((chip) => (
                  <Chip key={chip.id} onRemove={chip.remove} removeLabel={chip.label}>
                    {chip.label}
                  </Chip>
                ))}
                <button
                  type="button"
                  onClick={clear}
                  className="press ml-1 rounded-full px-2.5 py-1 text-xs font-medium text-faint transition hover:text-danger"
                >
                  Poništi sve
                </button>
              </div>
            )}

            {query.isError ? (
              <EmptyState
                title="Greška pri učitavanju"
                description="Nismo uspeli da učitamo oglase. Proverite internet konekciju i pokušajte ponovo."
                action={<Button onClick={() => query.refetch()}>Pokušaj ponovo</Button>}
              />
            ) : query.isPending ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {Array.from({ length: 6 }, (_, index) => (
                  <CarCardSkeleton key={index} index={index} />
                ))}
              </div>
            ) : results && results.items.length > 0 ? (
              <>
                <div
                  /*
                    Keyed by the query string so the whole grid remounts on a filter change and the
                    staggered entry replays. Without the key, React reuses the cards and the new
                    results simply appear, which is the moment the interface stops feeling alive.
                  */
                  key={searchParams.toString()}
                  className={cn(
                    'grid grid-cols-1 gap-4 transition-opacity duration-300 sm:grid-cols-2 xl:grid-cols-3',
                    query.isFetching && 'opacity-60',
                  )}
                >
                  {results.items.map((listing, index) => (
                    <CarCard key={listing.id} listing={listing} index={index} />
                  ))}
                </div>

                {results.totalPages > 1 && (
                  <nav className="mt-10 flex items-center justify-center gap-2" aria-label="Stranice">
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={results.page <= 1}
                      onClick={() => goToPage(results.page - 1)}
                    >
                      <ChevronLeft size={16} />
                      Prethodna
                    </Button>
                    <span className="px-3 text-sm text-muted tabular-nums">
                      {results.page} / {results.totalPages}
                    </span>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={results.page >= results.totalPages}
                      onClick={() => goToPage(results.page + 1)}
                    >
                      Sledeća
                      <ChevronRight size={16} />
                    </Button>
                  </nav>
                )}
              </>
            ) : (
              <EmptyState
                title="Nema rezultata"
                description={
                  activeCount > 0
                    ? 'Nijedan oglas ne odgovara izabranim filterima. Probajte da uklonite neki od njih.'
                    : 'Trenutno nema aktivnih oglasa.'
                }
                action={activeCount > 0 ? <Button onClick={clear}>Poništi filtere</Button> : undefined}
              />
            )}
          </div>
        </div>
      </div>

      {/* Bottom sheet on phones, side drawer from `sm` up, where a sidebar still has nowhere to live. */}
      <Sheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        title="Filteri"
        footer={
          <div className="flex gap-2">
            {activeCount > 0 && (
              <Button variant="secondary" size="lg" onClick={clear} className="shrink-0">
                Poništi
              </Button>
            )}
            <Button className="flex-1" size="lg" onClick={() => setSheetOpen(false)}>
              {/*
                The apply button carries the live result count, so the choice to close the filters is
                informed: nobody wants to dismiss a panel to discover they filtered down to zero.
              */}
              {results === undefined
                ? 'Prikaži rezultate'
                : `Prikaži ${formatListingCount(results.total)}`}
            </Button>
          </div>
        }
      >
        <FilterPanel
          filters={filters}
          setFilters={setFilters}
          toggleInArray={toggleInArray}
          toggleEquipment={toggleEquipment}
          clear={clear}
          activeCount={activeCount}
          hideHeader
        />
      </Sheet>

      {/*
        Below everything, and only on the landing view.

        Two things live here that the results grid cannot provide. The category links are the only
        route from the home page into the make and model pages, and without them those pages exist
        in the sitemap and nowhere else — which a search engine reads as exactly what it looks like.
        The questions are the visible half of the FAQ structured data the server emits for this URL;
        Google requires the answers in that markup to be on the page, so this section is what makes
        the markup honest rather than a violation.
      */}
      {activeCount === 0 && <BrowseByMake />}
      {activeCount === 0 && <HomeFaq />}
    </div>
  );
}

/** The make directory, as links rather than as filter buttons. */
function BrowseByMake() {
  const { data } = useSeoCatalogue();

  const makes = (data?.makes ?? []).slice(0, 16);

  if (makes.length === 0) {
    return null;
  }

  return (
    <section className="mx-auto max-w-7xl px-4 pb-12">
      <div className="flex items-end justify-between gap-3">
        <h2 className="font-display text-lg font-bold text-body">Polovni automobili po markama</h2>
        <Link
          to={categoryPath()}
          viewTransition
          className="shrink-0 text-sm font-medium text-accent transition hover:opacity-80"
        >
          Sve marke
        </Link>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
        {makes.map((make) => (
          <Link
            key={make.slug}
            to={categoryPath(make.slug)}
            viewTransition
            className="press flex items-center gap-2.5 rounded-xl border border-default bg-raised px-3 py-2.5 transition hover:border-accent hover:shadow-card"
          >
            <BrandMark make={{ name: make.name }} size="sm" className="shrink-0" />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium text-body">
                Polovni {make.name}
              </span>
              <span className="block text-[11px] text-faint">
                {formatListingCount(make.listingCount)}
              </span>
            </span>
          </Link>
        ))}
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <Link
          to={`/${BUY_GUIDE_SLUG}`}
          viewTransition
          className="press rounded-full border border-default bg-raised px-3.5 py-1.5 text-[13px] font-medium text-muted transition hover:border-accent hover:text-accent"
        >
          Kupujem auto
        </Link>
        <Link
          to={`/${SELL_GUIDE_SLUG}`}
          viewTransition
          className="press rounded-full border border-default bg-raised px-3.5 py-1.5 text-[13px] font-medium text-muted transition hover:border-accent hover:text-accent"
        >
          Prodajem auto
        </Link>
      </div>
    </section>
  );
}

function HomeFaq() {
  const { data } = useSeoContent();

  const entries = data?.homeFaq ?? [];

  if (entries.length === 0) {
    return null;
  }

  return (
    <section className="mx-auto max-w-3xl px-4 pb-14">
      <h2 className="font-display text-lg font-bold text-body">Česta pitanja</h2>

      <dl className="mt-4 divide-y divide-[var(--hairline)] border-y border-default">
        {entries.map((entry) => (
          <div key={entry.question} className="py-4">
            <dt className="text-[15px] font-semibold text-body">{entry.question}</dt>
            <dd className="mt-1.5 text-sm leading-relaxed text-muted">{entry.answer}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
